import { StandardEvents } from '@shopify/events';

/**
 * The add-to-cart button's label, when it carries a price.
 *
 * blocks/add-to-cart.liquid renders the wording and a first price straight from
 * the variant, so a page with no bundle picker on it is already correct and this
 * file changes nothing. Where a picker is present the two disagree — the picker
 * charges a quantity and sometimes a discount that Liquid could not know about —
 * and this listens for what the picker publishes and rewrites the figure.
 *
 * It never computes a price. The picker sends money already formatted, so there
 * is one formatter in the theme and one number on the page; a button that worked
 * out its own total could quietly promise something the cart would not charge.
 *
 * The separator belongs to the price rather than sitting between the two spans,
 * so hiding an unknown price takes the dash with it instead of leaving the
 * button reading "ADD TO CART —".
 */
class TgcAtcLabel extends HTMLElement {
  #host = null;

  connectedCallback() {
    this.#host = this.closest('.shopify-section') || document.body;
    this.#host.addEventListener('tgc:bundle:figures', this.#paint);
    // A variant change re-renders this button from the server and morphs it back
    // in, which writes Liquid's single-unit price over the set's. The picker
    // republishes at the same moment but only when its own figures changed, and
    // choosing a colour does not change what the set costs — so without this the
    // sticky button would quietly fall back to the price of one fan.
    this.#host.addEventListener(StandardEvents.productSelect, this.#afterThemeRender);
    // The picker publishes on its own first render, which on a section re-render
    // has already happened by the time this reconnects. Reading the dataset here
    // is what stops the button falling back to the single-unit price.
    this.#paint();
  }

  disconnectedCallback() {
    this.#host?.removeEventListener('tgc:bundle:figures', this.#paint);
    this.#host?.removeEventListener(StandardEvents.productSelect, this.#afterThemeRender);
  }

  /* productSelect fires before the morph lands, so the repaint has to happen on
     the far side of it — the same two frames the bundle picker waits. */
  #afterThemeRender = () => {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if (this.isConnected) this.#paint();
      })
    );
  };

  #paint = () => {
    const raw = this.#host?.dataset.tgcBundleFigures;
    // No picker on the page: leave the price Liquid rendered from the variant.
    if (!raw) return;

    let figures;
    try {
      figures = JSON.parse(raw);
    } catch {
      return;
    }

    // "Add to cart" says nothing about which of three bundles is about to be
    // added, and a shopper who chose the 3-Pack and reads "add to cart" beside
    // $93.00 has to work out for themselves that the two agree. The template
    // puts the count in the label, so the button states what it will do.
    const template = this.dataset.labelTemplate;
    const textEl = this.querySelector('[data-tgc-atc-text]');
    if (template && textEl && figures.units > 0) {
      const plural = figures.units === 1 ? '' : 's';
      // [S] as well as [s], so a label written in capitals stays in capitals:
      // "ADD [n] FAN[S]" has to give ADD 2 FANS and not ADD 2 FANs.
      const next = template
        .replace(/\[n\]/g, String(figures.units))
        .replace(/\[s\]/g, plural)
        .replace(/\[S\]/g, plural.toUpperCase());
      if (textEl.textContent !== next) textEl.textContent = next;
    }

    // The label is worth updating on its own: a button set to carry the count
    // but not the price has no price element at all, and an early return on a
    // missing one used to take the label with it.
    const priceEl = this.querySelector('[data-tgc-atc-price]');
    if (!priceEl) return;

    const source = this.dataset.source || 'total';
    const amount = figures.available ? figures.money?.[source] : null;

    // A saving of nothing is not worth a slot on the button, and neither is a
    // set whose price cannot be resolved.
    const blank = !amount || ((source === 'save_amount' || source === 'save_percent') && !figures.saving);

    if (priceEl.hidden !== blank) priceEl.hidden = blank;
    if (blank) return;

    const separator = this.dataset.separator || '';
    // The space rides with the separator, so clearing the separator in the
    // theme editor does not leave the price starting with one.
    const next = (separator ? ' ' + separator + ' ' : '') + amount;
    if (priceEl.textContent !== next) priceEl.textContent = next;
  };
}

if (!customElements.get('tgc-atc-label')) customElements.define('tgc-atc-label', TgcAtcLabel);
